export function StatCard({ label, value, icon: Icon, accent = "orange", testid }) {
  const colors = {
    orange: "text-orange-400 bg-orange-500/10", emerald: "text-emerald-400 bg-emerald-500/10",
    blue: "text-blue-400 bg-blue-500/10", amber: "text-amber-400 bg-amber-500/10",
    red: "text-red-400 bg-red-500/10", violet: "text-violet-400 bg-violet-500/10",
  };
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        {Icon && <span className={`w-9 h-9 rounded-lg grid place-items-center ${colors[accent]}`}><Icon className="w-5 h-5" /></span>}
      </div>
      <div className="text-2xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

export function PageTitle({ title, desc, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div>
        <h1 className="font-display text-2xl font-bold">{title}</h1>
        {desc && <p className="text-sm text-slate-400 mt-1">{desc}</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

const BADGES = {
  published: "bg-emerald-500/15 text-emerald-400", draft: "bg-slate-500/15 text-slate-400",
  confirmed: "bg-emerald-500/15 text-emerald-400", completed: "bg-blue-500/15 text-blue-400",
  payment_pending: "bg-amber-500/15 text-amber-400", payment_verification: "bg-amber-500/15 text-amber-400",
  cancelled: "bg-red-500/15 text-red-400", paid: "bg-emerald-500/15 text-emerald-400",
  pending: "bg-amber-500/15 text-amber-400", submitted: "bg-blue-500/15 text-blue-400",
  failed: "bg-red-500/15 text-red-400", refunded: "bg-violet-500/15 text-violet-400",
  requested: "bg-amber-500/15 text-amber-400", processing: "bg-blue-500/15 text-blue-400",
  rejected: "bg-red-500/15 text-red-400", not_initiated: "bg-slate-500/15 text-slate-300", not_eligible: "bg-slate-500/15 text-slate-400",
};

export function Badge({ status }) {
  if (!status) return <span className="text-slate-500">—</span>;
  return <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full capitalize ${BADGES[status] || "bg-slate-500/15 text-slate-400"}`}>{String(status).replace(/_/g, " ")}</span>;
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-slate-800 border border-slate-700 rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} my-8`} onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 sticky top-0 bg-slate-800 rounded-t-2xl">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl" data-testid="modal-close">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function TableWrap({ children }) {
  return <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">{children}</table></div></div>;
}

export function Input({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      {label && <label className="block text-sm font-semibold text-slate-300 mb-1">{label}</label>}
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid={testid} />
    </div>
  );
}

export function Textarea({ label, value, onChange, rows = 3, testid }) {
  return (
    <div>
      {label && <label className="block text-sm font-semibold text-slate-300 mb-1">{label}</label>}
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid={testid} />
    </div>
  );
}

export function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      {label && <label className="block text-sm font-semibold text-slate-300 mb-1">{label}</label>}
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid={testid}>
        {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", type = "button", testid, disabled, className = "" }) {
  const styles = {
    primary: "bg-orange-600 hover:bg-orange-700 text-white",
    outline: "border border-slate-600 text-slate-200 hover:bg-slate-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    ghost: "text-slate-300 hover:bg-slate-700",
  };
  return <button type={type} onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-2 font-semibold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-40 ${styles[variant]} ${className}`} data-testid={testid}>{children}</button>;
}

// Calendar-based date range filter (native date pickers).
export function DateRange({ from, to, onChange, testid = "date-range" }) {
  const cls = "rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 [color-scheme:dark]";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <span className="text-xs text-slate-400">Seva date</span>
      <input type="date" value={from} max={to || undefined} onChange={(e) => onChange({ from: e.target.value, to })} className={cls} aria-label="From date" data-testid={`${testid}-from`} />
      <span className="text-slate-500 text-sm">to</span>
      <input type="date" value={to} min={from || undefined} onChange={(e) => onChange({ from, to: e.target.value })} className={cls} aria-label="To date" data-testid={`${testid}-to`} />
      {(from || to) && <button type="button" onClick={() => onChange({ from: "", to: "" })} className="text-xs text-orange-400 hover:underline" data-testid={`${testid}-clear`}>Clear</button>}
    </div>
  );
}
